// Gift-Wish - Main Application Entry Point
import { useState, useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import api from "@/utils/api";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Contexts
import { LanguageProvider, AuthProvider, useLanguage, useAuth } from "@/contexts";

// Critical pages - loaded immediately
import LandingPage from "@/pages/LandingPage";

// Lazy loaded pages - split code for faster initial load
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const EventPage = lazy(() => import("@/pages/EventPage"));
const PublicWishlist = lazy(() => import("@/pages/PublicWishlist"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const GroupsPage = lazy(() => import("@/pages/GroupsPage"));
const GroupDetailPage = lazy(() => import("@/pages/GroupDetailPage"));
const InvitationsPage = lazy(() => import("@/pages/InvitationsPage"));
const MyListsPage = lazy(() => import("@/pages/MyListsPage"));
const JoinGroupPage = lazy(() => import("@/pages/JoinGroupPage"));
const AdminPanel = lazy(() => import("@/AdminPanel"));
const ResetPasswordPage = lazy(() => import("@/ResetPasswordPage"));

// Other Components - keep sync as they're small
import InstallPWA from "@/InstallPWA";
import CookieConsent from "@/components/CookieConsent";
import SEO from "@/components/SEO";
import ErrorBoundary from "@/components/ErrorBoundary";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Re-export hooks for backward compatibility
export { useLanguage, useAuth } from "@/contexts";

// Auth Callback - handles OAuth redirect
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const { t } = useLanguage();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/");
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await api.post(
          `/auth/session`,
          { session_id: sessionId }
        );

        // Save tokens to localStorage
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        if (response.data.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }

        setUser(response.data.user);
        navigate("/dashboard", { replace: true, state: { user: response.data.user } });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error(t("error_auth"));
        navigate("/");
      }
    };

    processAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">{t("loading")}</p>
      </div>
    </div>
  );
};

// Auth Callback for Popup (Google OAuth)
const AuthCallbackPopup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const hash = location.hash || window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      // If opened in popup, send message to parent
      if (window.opener) {
        window.opener.postMessage({
          type: 'google_auth_success',
          sessionId: sessionId
        }, window.location.origin);
        window.close();
        return;
      }
      
      // If redirect (mobile), handle auth directly
      const handleRedirectAuth = async () => {
        try {
          const response = await api.post(`/auth/session`, { session_id: sessionId });
          
          // Save tokens to localStorage
          if (response.data.session_token) {
            localStorage.setItem('session_token', response.data.session_token);
          }
          if (response.data.refresh_token) {
            localStorage.setItem('refresh_token', response.data.refresh_token);
          }
          
          // Update user state
          setUser(response.data.user);
          
          // Clear pending flag
          localStorage.removeItem('auth_redirect_pending');
          
          // Redirect to dashboard
          navigate('/dashboard', { replace: true });
        } catch (err) {
          console.error('Auth callback error:', err);
          setError('Ошибка авторизации. Попробуйте снова.');
          localStorage.removeItem('auth_redirect_pending');
        }
      };
      
      handleRedirectAuth();
    }
  }, [location, navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <button 
            onClick={() => navigate('/')} 
            className="text-accent hover:underline"
          >
            Вернуться на главную
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Завершение авторизации...</p>
      </div>
    </div>
  );
};

// Protected Route - requires authentication
const ProtectedRoute = ({ children }) => {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.user) return;
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && !location.state?.user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user && !location.state?.user) {
    navigate("/");
    return null;
  }

  return children;
};

// App Router - handles all routes
function AppRouter() {
  const location = useLocation();
  
  // Check if this is a popup callback
  if (location.pathname === '/auth/callback' && location.hash?.includes('session_id=')) {
    return <AuthCallbackPopup />;
  }
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/event/:eventId" element={
          <ProtectedRoute>
            <EventPage />
          </ProtectedRoute>
        } />
        <Route path="/wishlist/:shareCode" element={<PublicWishlist />} />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminPanel />
          </ProtectedRoute>
        } />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/groups" element={
          <ProtectedRoute>
            <GroupsPage />
          </ProtectedRoute>
        } />
        <Route path="/group/:groupId" element={
          <ProtectedRoute>
            <GroupDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/invitations" element={
          <ProtectedRoute>
            <InvitationsPage />
          </ProtectedRoute>
        } />
        <Route path="/my-lists" element={
          <ProtectedRoute>
            <MyListsPage />
          </ProtectedRoute>
        } />
        <Route path="/join/:inviteCode" element={
          <ProtectedRoute>
            <JoinGroupPage />
          </ProtectedRoute>
        } />
        <Route path="/auth/callback" element={<AuthCallbackPopup />} />
      </Routes>
    </Suspense>
  );
}

// Main App Component
function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <SEO />
            <ErrorBoundary>
              <AppRouter />
            </ErrorBoundary>
            <Toaster position="top-center" duration={4000} />
            <InstallPWA />
            <CookieConsent />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
